/**
 * Resolve the reference price for alert evaluation.
 *
 * W1.1 — sửa lỗi toán học. Bản gốc coi `point.y` là **giá** và nội suy bằng
 * `nowMs` (epoch ms) trên trục `point.x`. Nhưng `Point.y` trong hệ thống vẽ là
 * **pixel Y**; giá thật nằm ở `Point.yPrice` và thời gian ở `Point.timeMsHint`
 * (hai field này tồn tại đúng cho mục đích này nhưng evaluator không dùng).
 * Với `nowMs ≈ 1.75e12` và `p1.x` là pixel (0..2000), biểu thức cũ trả về một
 * con số vô nghĩa ⇒ alert bắn ở mức giá sai.
 *
 * Thứ tự ưu tiên:
 *  1. `hLine` (đường ngang) ⇒ `yPrice` nếu có, không có thì `y`.
 *  2. Đường chéo có **cả** `yPrice` và `timeMsHint` trên cả hai điểm ⇒ nội suy
 *     tuyến tính theo **thời gian**, dùng `nowMs`.
 *  3. Đường chéo thiếu `timeMsHint` ⇒ **trả null** (không bắn). Ngoại suy theo
 *     đồng hồ ở đây là vô nghĩa; "không bắn" đúng hơn "bắn sai".
 *  4. Không có `yPrice` nào ⇒ fallback `y` để tương thích drawing cũ (hành vi
 *     cũ, và là hành vi mà test DT10-T02-01/02 khoá lại).
 */
export function resolveReferencePriceForAlert(drawing, nowMs = Date.now()) {
    if (drawing.points.length === 0)
        return null;
    const first = drawing.points[0];
    // Đường ngang: một mức giá, không cần nội suy.
    if (drawing.type === "hLine") {
        return first.yPrice ?? first.y;
    }
    if (drawing.points.length === 1)
        return first.yPrice ?? first.y;
    if (drawing.points.length >= 2) {
        const p1 = drawing.points[0];
        const p2 = drawing.points[1];
        // Đường chéo: chỉ nội suy được khi CẢ HAI điểm có giá và mốc thời gian.
        const hasPrice = p1.yPrice != null && p2.yPrice != null;
        const hasTime = p1.timeMsHint != null && p2.timeMsHint != null;
        if (hasPrice && hasTime) {
            const dt = p2.timeMsHint - p1.timeMsHint;
            if (dt === 0)
                return p1.yPrice;
            const t = ((nowMs - p1.timeMsHint) / dt) * (p2.yPrice - p1.yPrice);
            return p1.yPrice + t;
        }
        // Có giá nhưng không có mốc thời gian: KHÔNG ngoại suy theo đồng hồ.
        // Trả null để caller bỏ qua, thay vì bắn ở mức giá vô nghĩa.
        if (hasPrice)
            return null;
    }
    // Drawing cũ không có `yPrice`: giữ hành vi cũ (nội suy trên `x`/`y` thô).
    const p1 = drawing.points[0];
    const p2 = drawing.points[1];
    const dx = p2.x - p1.x;
    if (dx === 0)
        return p1.y;
    return p1.y + ((nowMs - p1.x) / dx) * (p2.y - p1.y);
}
/** Default cooldown: 60 seconds */
const DEFAULT_COOLDOWN_MS = 60_000;
/**
 * Check if cooldown has elapsed since last trigger.
 * DT-10-T04: cooldown logic.
 */
export function isCooldownElapsed(condition, nowMs) {
    if (!condition.triggeredAt)
        return true;
    const cooldown = condition.cooldownMs ?? DEFAULT_COOLDOWN_MS;
    return nowMs - condition.triggeredAt >= cooldown;
}
/**
 * Determine if a price cross has occurred.
 * previousPrice → currentPrice crossing referencePrice in the specified direction.
 */
export function detectPriceCross(previousPrice, currentPrice, referencePrice, direction) {
    const crossedAbove = previousPrice <= referencePrice && currentPrice > referencePrice;
    const crossedBelow = previousPrice >= referencePrice && currentPrice < referencePrice;
    switch (direction) {
        case "crossAbove":
            return crossedAbove;
        case "crossBelow":
            return crossedBelow;
        case "either":
            return crossedAbove || crossedBelow;
    }
}
/**
 * DT-10-T03: Evaluate all drawings with alert conditions.
 * Mutates `condition.triggeredAt` on each fired alert.
 * Returns the list of fired alert events.
 */
export function evaluateDrawingAlerts(drawings, previousPrice, currentPrice, nowMs = Date.now()) {
    const fired = [];
    for (const drawing of drawings) {
        // Collect active alert conditions (GAP-14 support for both single and multiple alerts)
        const activeConditions = [];
        if (drawing.alertConditions && drawing.alertConditions.length > 0) {
            activeConditions.push(...drawing.alertConditions);
        }
        else if (drawing.alertCondition) {
            activeConditions.push(drawing.alertCondition);
        }
        for (const condition of activeConditions) {
            if (!condition || !condition.enabled)
                continue;
            const refPrice = resolveReferencePriceForAlert(drawing, nowMs);
            if (refPrice === null)
                continue;
            if (!isCooldownElapsed(condition, nowMs))
                continue;
            const triggered = detectPriceCross(previousPrice, currentPrice, refPrice, condition.direction);
            if (!triggered)
                continue;
            const actualDirection = currentPrice > previousPrice ? "crossAbove" : "crossBelow";
            // Mutate triggeredAt so caller can persist update
            condition.triggeredAt = nowMs;
            fired.push({
                drawing,
                referencePrice: refPrice,
                currentPrice,
                direction: actualDirection,
                triggeredAt: nowMs,
                condition,
            });
        }
    }
    return fired;
}
